/**
 * iOS Platform Implementation
 * Contains all iOS bridge generation logic moved from BridgeGenerator
 */

import fs from "fs-extra";
import path from "path";
import pc from "picocolors";
import type { ComponentConfig } from "../main-index";
import type {
  ModuleConstant,
  ModuleEvent,
  ModuleMethod,
  ParsedModule,
} from "../parser";
import type { ScannedModule } from "../scanner";
import {
  createSwiftBridgeGenerator,
  createSwiftCodeEmitter,
  createSwiftGeneratorSuite,
  createSwiftMethodGenerator,
  createSwiftProtocolGenerator,
  createSwiftStructGenerator,
  createSwiftTypeMapper,
} from "../syntax/swift";
import type { IOSPlatformGeneratorInterface } from "./platform-interface";

export interface BridgeGenerationResult {
  generatedFiles: string[];
  warnings: string[];
}

export class IOSPlatformGenerator implements IOSPlatformGeneratorInterface {
  private config: ComponentConfig;
  private scannedModules: ScannedModule[] = [];
  private typeMapper: ReturnType<typeof createSwiftTypeMapper>;
  private bridgeGenerator: ReturnType<typeof createSwiftBridgeGenerator>;
  private methodGenerator: ReturnType<typeof createSwiftMethodGenerator>;
  private protocolGenerator: ReturnType<typeof createSwiftProtocolGenerator>;
  private structGenerator: ReturnType<typeof createSwiftStructGenerator>;

  constructor(config: ComponentConfig) {
    this.config = config;
    this.typeMapper = createSwiftTypeMapper();
    const codeEmitter = createSwiftCodeEmitter();
    this.methodGenerator = createSwiftMethodGenerator({
      typeMapper: this.typeMapper,
    });
    this.bridgeGenerator = createSwiftBridgeGenerator({
      typeMapper: this.typeMapper,
      codeEmitter,
    });
    this.protocolGenerator = createSwiftProtocolGenerator({
      typeMapper: this.typeMapper,
      codeEmitter,
    });
    this.structGenerator = createSwiftStructGenerator({
      typeMapper: this.typeMapper,
      codeEmitter,
    });
  }

  getPlatformName(): "ios" {
    return "ios";
  }

  supports(target: string): boolean {
    return target === "ios" || target === "swift" || target === "objective-c";
  }

  /**
   * Main method - Generates iOS bridge files (moved from BridgeGenerator.generateIOSBridge)
   */
  async generateBridge(
    modules: ParsedModule[],
    scannedModules?: ScannedModule[]
  ): Promise<string[]> {
    // Store scanned modules for use in generatePodspecDependencies
    if (scannedModules) {
      this.scannedModules = scannedModules;
    }
    try {
      // Generate files in configured iOS output directory
      const iosBrickDir = path.join(
        this.config.projectRoot,
        this.config.outputIos
      );
      const generatedFiles: string[] = [];

      await fs.ensureDir(iosBrickDir);

      // Generate BrickCodegen.h umbrella header file
      try {
        const umbrellaHeaderFile = await this.generateBrickCodegenHeader(
          iosBrickDir
        );
        generatedFiles.push(umbrellaHeaderFile);
      } catch (error) {
        throw new Error(
          `Failed in generateBrickCodegenHeader: ${(error as Error).message}`
        );
      }

      // Generate BrickModule.h header file (include typed event wrappers)
      try {
        const allEvents = this.getAllEvents(modules) || [];
        const headerFile = await this.generateBrickModuleHeader(
          iosBrickDir,
          allEvents
        );
        generatedFiles.push(headerFile);
      } catch (error) {
        throw new Error(
          `Failed in generateBrickModuleHeader: ${(error as Error).message}`
        );
      }

      // Generate Objective-C bridge (BrickModule.mm)
      try {
        const bridgeFile = await this.generateObjectiveCBridge(
          modules,
          iosBrickDir
        );
        generatedFiles.push(bridgeFile);
      } catch (error) {
        throw new Error(
          `Failed in generateObjectiveCBridge: ${(error as Error).message}`
        );
      }

      // Generate Swift implementation files (BrickModule.swift and extensions)
      try {
        const implFiles = await this.generateSwiftImplementationFiles(
          modules,
          iosBrickDir
        );
        generatedFiles.push(...implFiles);
      } catch (error) {
        throw new Error(
          `Failed in generateSwiftImplementation: ${(error as Error).message}`
        );
      }

      // Generate local module types in .brick/Types/ directory
      try {
        const localTypeFiles = await this.generateLocalModuleTypes(
          modules,
          iosBrickDir
        );
        generatedFiles.push(...localTypeFiles);
      } catch (error) {
        throw new Error(
          `Failed in generateLocalModuleTypes: ${(error as Error).message}`
        );
      }

      // Note: Library types and protocols are now generated by each library in library mode
      // This ensures proper separation between user projects and library packages

      // Generate BrickCodegen.podspec
      try {
        const podspecFile = await this.generatePodspec();
        generatedFiles.push(podspecFile);
      } catch (error) {
        throw new Error(
          `Failed in generatePodspec: ${(error as Error).message}`
        );
      }

      return generatedFiles;
    } catch (error) {
      throw new Error(
        `Failed to generate iOS bridge: ${(error as Error).message}`
      );
    }
  }

  isAvailable(): boolean {
    return true;
  }

  /**
   * Generates BrickCodegen.h umbrella header file
   */
  private async generateBrickCodegenHeader(outputDir: string): Promise<string> {
    const headerContent = this.bridgeGenerator.generateBrickCodegenHeader();

    const outputPath = path.join(outputDir, "BrickCodegen.h");
    await fs.writeFile(outputPath, headerContent);
    this.logGenerated(outputPath);
    return outputPath;
  }

  /**
   * Generates BrickModule.h header file
   */
  private async generateBrickModuleHeader(
    outputDir: string,
    events: Array<ModuleEvent & { moduleName: string }>
  ): Promise<string> {
    const headerContent =
      this.bridgeGenerator.generateBrickModuleHeader(events);

    const outputPath = path.join(outputDir, "BrickModule.h");
    await fs.writeFile(outputPath, headerContent);
    this.logGenerated(outputPath);
    return outputPath;
  }

  /**
   * Generates the main Objective-C bridge file (BrickModule.mm)
   */
  private async generateObjectiveCBridge(
    modules: ParsedModule[],
    outputDir: string
  ): Promise<string> {
    const allMethods = this.getAllMethods(modules) || [];
    const allConstants = this.getAllConstants(modules) || [];
    const allEvents = this.getAllEvents(modules) || [];

    const bridgeContent = this.bridgeGenerator.generateObjectiveCBridge(
      allMethods,
      allConstants,
      allEvents
    );

    const outputPath = path.join(outputDir, "BrickModule.mm");

    await fs.writeFile(outputPath, bridgeContent);
    this.logGenerated(outputPath);

    return outputPath;
  }

  /**
   * Generates all Swift implementation files
   */
  private async generateSwiftImplementationFiles(
    modules: ParsedModule[],
    outputDir: string
  ): Promise<string[]> {
    const generatedFiles: string[] = [];

    // Generate BrickModuleImpl.swift (Swift implementation holder)
    const implClassFile = await this.generateBrickModuleImplSwift(outputDir);
    generatedFiles.push(implClassFile);

    // Generate main BrickModule.swift (utility + helpers on Impl)
    const mainFile = await this.generateBrickModuleSwift(modules, outputDir);
    generatedFiles.push(mainFile);

    // Generate extension files for each module (BrickModule+ModuleName.swift)
    for (const module of modules) {
      const extFile = await this.generateModuleExtension(module, outputDir);
      generatedFiles.push(extFile);
    }

    // Generate utility extensions if needed
    // Add more extension files generation here if needed

    return generatedFiles;
  }

  /**
   * Generates main BrickModule.swift file
   */
  private async generateBrickModuleSwift(
    modules: ParsedModule[],
    outputDir: string
  ): Promise<string> {
    const allEvents = this.getAllEvents(modules);
    const swiftContent =
      this.bridgeGenerator.generateBrickModuleSwift(allEvents);

    const outputPath = path.join(outputDir, "BrickModule.swift");
    await fs.writeFile(outputPath, swiftContent);
    this.logGenerated(outputPath);
    return outputPath;
  }

  /**
   * Helper method to get Swift default value for a type
   */
  private getSwiftDefaultValue(swiftType: string): string {
    return this.typeMapper.getSwiftDefaultValue(swiftType);
  }

  /**
   * Generates module-specific extension file (BrickModule+ModuleName.swift)
   */
  private async generateModuleExtension(
    module: ParsedModule,
    outputDir: string
  ): Promise<string> {
    const moduleMethods = module.methods;
    const moduleConstants = module.constants;

    // Generate type aliases for this module's types
    const typeAliases = this.generateModuleTypeAliases(module);

    // Generate method implementations for this module
    const methodImplementations = moduleMethods
      .map((method) => this.generateSwiftModuleMethod(method, module))
      .join("\n\n    ");

    // Generate constant getters for this module
    const constantGetters = moduleConstants
      .map((constant) =>
        this.generateModuleConstantGetter(constant, module.moduleName)
      )
      .join("\n\n    ");

    // Check if this is a local module (no brickConfig)
    const isLocalModule = !module.sourceModule.brickConfig;
    const cocoapodsModuleName = isLocalModule
      ? null
      : module.sourceModule.brickConfig?.ios?.moduleName;

    // Only import the cocoapods module if it exists (not a local module)
    const importStatement = cocoapodsModuleName
      ? `import ${cocoapodsModuleName}`
      : "";

    const swiftContent = `//
//  BrickModule+${module.moduleName}.swift
//  Pods
//

import Foundation
import BrickModule
import React
${importStatement}

${typeAliases}

extension BrickModuleImpl {

    ${methodImplementations}

    ${constantGetters}

}
`;

    const outputPath = path.join(
      outputDir,
      `BrickModule+${module.moduleName}.swift`
    );
    await fs.writeFile(outputPath, swiftContent);
    this.logGenerated(outputPath);
    return outputPath;
  }

  /**
   * Generates the BrickModuleImpl.swift implementation holder
   */
  private async generateBrickModuleImplSwift(
    outputDir: string
  ): Promise<string> {
    const content = `import Foundation
import React
import BrickModule

@objc public class BrickModuleImpl: NSObject {
    @objc public var brickModuleRegistry: BrickModuleRegistry? {
        didSet {
            // When registry is injected via KVC, set the converted event map
            if let convertedMap = _convertedEventMap {
                brickModuleRegistry?.setEventMap(convertedMap)
            }
            if let bridge = _bridge {
                brickModuleRegistry?.setBridge(bridge)
            }
        }
    }
    private var _convertedEventMap: [String: [String: ([String: Any]) -> Void]]?
    private var _bridge: Any?

    @objc public override init() {
        super.init()
    }

    @objc public func setBridge(_ bridge: Any) {
        self._bridge = bridge
        self.brickModuleRegistry?.setBridge(bridge)
    }

    @objc public func setEventMap(_ map: NSDictionary) {
        typealias BlockType = @convention(block) (NSDictionary) -> Void

        let convertedMap = (map as? [String: Any])?.compactMapValues { moduleEventsAny -> [String: ([String: Any]) -> Void]? in
            guard let eventDict = moduleEventsAny as? [String: Any] else { return nil }

            return eventDict.compactMapValues { eventBlock -> (([String: Any]) -> Void)? in
                // Store block as AnyObject to maintain reference
                let blockObject = eventBlock as AnyObject

                // Convert Objective-C block to Swift closure
                let block = unsafeBitCast(blockObject, to: BlockType.self)

                return { (payload: [String: Any]) in
                    block(payload as NSDictionary)
                }
            }
        } ?? [:]

        self._convertedEventMap = convertedMap
        self.brickModuleRegistry?.setEventMap(convertedMap)
    }

    @objc public func getRegisteredModules() -> [String] {
        return brickModuleRegistry?.getRegisteredModules() ?? []
    }
}
`;

    const outputPath = path.join(outputDir, "BrickModuleImpl.swift");
    await fs.writeFile(outputPath, content);
    this.logGenerated(outputPath);
    return outputPath;
  }

  /**
   * Generates type aliases for a specific module
   */
  private generateModuleTypeAliases(module: ParsedModule): string {
    return this.protocolGenerator.generateModuleTypeAliases(module);
  }

  /**
   * Generates a Swift method implementation for a specific module
   */
  private generateSwiftModuleMethod(
    method: ModuleMethod,
    module: ParsedModule
  ): string {
    return this.methodGenerator.generateSwiftModuleMethod(method, module);
  }

  /**
   * Generates a constant getter for a specific module
   */
  private generateModuleConstantGetter(
    constant: ModuleConstant,
    moduleName: string
  ): string {
    return this.methodGenerator.generateModuleConstantGetter(
      constant,
      moduleName
    );
  }

  /**
   * Generates local module types in .brick/Types/ directory
   */
  private async generateLocalModuleTypes(
    modules: ParsedModule[],
    outputDir: string
  ): Promise<string[]> {
    const generatedFiles: string[] = [];
    const typesDir = path.join(outputDir, "Types");

    // Only process local modules (those without brickConfig)
    const localModules = modules.filter((m) => !m.sourceModule?.brickConfig);

    if (localModules.length === 0) {
      return generatedFiles;
    }

    // Ensure Types directory exists
    await fs.ensureDir(typesDir);

    for (const module of localModules) {
      // Generate protocol file
      let protocolContent = await this.generateLibraryProtocol(module);

      // Append event helpers if module has events
      if (module.events && module.events.length > 0) {
        const eventHelpers = this.generateEventHelpersExtension(module);
        protocolContent += `\n${eventHelpers}`;
      }

      const protocolFile = path.join(
        typesDir,
        `${module.moduleName}Spec.swift`
      );
      await fs.writeFile(protocolFile, protocolContent);
      generatedFiles.push(protocolFile);
      this.logGenerated(protocolFile);

      // Generate types file if there are interface definitions
      if (module.interfaceDefinitions.length > 0) {
        const typesContent = await this.generateLibraryTypes(module);
        const typesFile = path.join(
          typesDir,
          `${module.moduleName}Types.swift`
        );
        await fs.writeFile(typesFile, typesContent);
        generatedFiles.push(typesFile);
        this.logGenerated(typesFile);
      }
    }

    return generatedFiles;
  }

  /**
   * Generates event helper extension for protocol
   * Used by both library and local modules
   */
  private generateEventHelpersExtension(module: ParsedModule): string {
    const helpers = (module.events || [])
      .map((e) => {
        const cap = e.name.charAt(0).toUpperCase() + e.name.slice(1);
        const eventKey = `on${cap}`;
        return `    public func emitOn${cap}(payload: [String: Any]) {\n        emit("${eventKey}", payload: payload)\n    }`;
      })
      .join("\n\n");

    return `// Typed Event Emit Helpers for ${module.moduleName}
extension ${module.moduleName}Spec where Self: BrickModuleBase {
${helpers}
}
`;
  }

  // REMOVED: generateSwiftImplementation method - replaced by generateSwiftImplementationFiles
  // which generates BrickModule.swift and BrickModule+ModuleName.swift files

  /**
   * Generates Swift types for library mode (public API)
   */
  async generateLibraryTypes(module: ParsedModule): Promise<string> {
    const suite = createSwiftGeneratorSuite({
      typeMappingOptions: {
        moduleName: module.moduleName,
        interfaces: module.interfaceDefinitions,
      },
    });
    return suite.generateStructs(module);
  }

  /**
   * Generates Swift protocol for library mode (public API)
   */
  async generateLibraryProtocol(module: ParsedModule): Promise<string> {
    const suite = createSwiftGeneratorSuite({
      typeMappingOptions: {
        moduleName: module.moduleName,
        interfaces: module.interfaceDefinitions,
      },
    });
    return suite.generateProtocol(module);
  }

  /**
   * Generates BrickModule.podspec for iOS integration with automatic dependencies
   */
  private async generatePodspec(): Promise<string> {
    // Generate dynamic dependencies based on discovered modules
    const dependencies = await this.generatePodspecDependencies();

    const podspecContent = `folly_compiler_flags = '-DFOLLY_NO_CONFIG -DFOLLY_MOBILE=1 -DFOLLY_USE_LIBCPP=1 -DFOLLY_CFG_NO_COROUTINES=1 -Wno-comma -Wno-shorten-64-to-32'

Pod::Spec.new do |s|
  s.name         = "BrickCodegen"
  s.version      = "1.0.0"
  s.summary      = "Generated Brick Module bridge code"
  s.description  = "Auto-generated bridge code for Brick modules by brick-codegen"
  s.homepage     = "https://github.com/toss/brick"
  s.license      = "MIT"
  s.platforms    = { :ios => '13.0' }
  s.author       = { "brick-codegen" => "toss" }
  s.source       = { :git => ".", :tag => "#{s.version}" }

  s.swift_version = "5.0"
  s.module_name = "BrickCodegen"

  s.source_files = "**/*.{h,m,mm,swift}", "Types/**/*.swift"
  s.public_header_files = "BrickCodegen.h"
  s.preserve_paths = "**/*"

  s.dependency "BrickModule"
${dependencies}

  install_modules_dependencies(s)
end`;

    const iosBrickDir = path.join(
      this.config.projectRoot,
      this.config.outputIos
    );
    const outputPath = path.join(iosBrickDir, "BrickCodegen.podspec");

    await fs.ensureDir(iosBrickDir);
    await fs.writeFile(outputPath, podspecContent);
    this.logGenerated(outputPath);

    return outputPath;
  }

  /**
   * Generates podspec dependencies based on discovered Brick modules
   * Uses the scanned modules from Scanner instead of re-scanning
   */
  private async generatePodspecDependencies(): Promise<string> {
    const dependencies: string[] = [];

    try {
      // Use the scanned modules that were passed from main-index.ts
      for (const module of this.scannedModules) {
        // Only process modules that have iOS implementation
        if (
          module.hasIosImplementation &&
          module.brickConfig?.ios?.moduleName
        ) {
          const cocoapodsModuleName = module.brickConfig.ios.moduleName;
          dependencies.push(`  s.dependency "${cocoapodsModuleName}"`);
        }
      }
    } catch (error) {
      // If there's an error, return empty dependencies
      console.warn(`Warning: Could not process Brick dependencies: ${error}`);
    }

    return dependencies.join("\n");
  }

  // Helper methods

  private getAllMethods(
    modules: ParsedModule[]
  ): Array<ModuleMethod & { moduleName: string }> {
    const methods: Array<ModuleMethod & { moduleName: string }> = [];

    if (!modules || !Array.isArray(modules)) {
      return methods;
    }

    for (const module of modules) {
      if (!module || !module.methods || !Array.isArray(module.methods)) {
        continue;
      }

      for (const method of module.methods) {
        methods.push({
          ...method,
          moduleName: module.moduleName,
        });
      }
    }

    return methods;
  }

  private getAllEvents(
    modules: ParsedModule[]
  ): Array<ModuleEvent & { moduleName: string }> {
    const events: Array<ModuleEvent & { moduleName: string }> = [];

    if (!modules || !Array.isArray(modules)) {
      return events;
    }

    for (const module of modules) {
      if (!module || !module.events || !Array.isArray(module.events)) {
        continue;
      }

      for (const event of module.events) {
        events.push({
          ...event,
          moduleName: module.moduleName,
        });
      }
    }

    return events;
  }

  private getAllConstants(
    modules: ParsedModule[]
  ): Array<ModuleConstant & { moduleName: string }> {
    const constants: Array<ModuleConstant & { moduleName: string }> = [];

    if (!modules || !Array.isArray(modules)) {
      return constants;
    }

    for (const module of modules) {
      if (!module || !module.constants || !Array.isArray(module.constants)) {
        continue;
      }

      for (const constant of module.constants) {
        constants.push({
          ...constant,
          moduleName: module.moduleName,
        });
      }
    }

    return constants;
  }

  private logGenerated(filepath: string): void {
    const absolutePath = path.resolve(filepath);
    console.log(
      `${pc.bold(pc.green("[Brick]"))} Generated artifact: ${absolutePath}`
    );
  }
}
